import axios from 'axios';
import { serverSide } from 'libs/utils/environment';
import { getLocale, logWarning, navigateToPage } from 'libs/utils/window';
import { getCookie } from 'libs/utils/cookies';
import { REFRESH_ACCESS_TOKEN_API_ENDPOINT } from 'constants/paths';
import { ANON_ID_COOKIE_NAME } from 'constants/cookie';
import { ANON_ID_HEADER_NAME } from 'constants/header';
import { HttpStatus, ResponseCode } from 'data/api/response-codes';
import { PHONE_VERIFICATION_URL } from 'constants/routes';
import { TWO_FA_CANCELLED_EVENT, TWO_FA_COMPLETED_EVENT } from 'constants/index';
import { extractErrorResponseCode } from 'data/utils/axios';
export const railsCsrfTokenInterceptor = instance => instance.interceptors.request.use(config => {
  var _document$querySelect;

  if (serverSide) return config;
  const csrfToken = (_document$querySelect = document.querySelector('meta[name="csrf-token"]')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.getAttribute('content');
  if (csrfToken) config.headers.set('X-CSRF-Token', csrfToken);
  return config;
});
export const nextJsCsrfTokenInterceptor = instance => instance.interceptors.request.use(config => {
  const csrfToken = process.env.NEXT_PUBLIC_CSRF_TOKEN;
  if (!csrfToken) throw new Error('Missing Next.js CSRF token');
  config.headers.set('X-CSRF-Token', csrfToken);
  return config;
});
export const csrfTokenInterceptor = process.env.NEXT_PUBLIC_NEXT_JS === 'true' ? nextJsCsrfTokenInterceptor : railsCsrfTokenInterceptor;
export const tokenRefreshInterceptor = instance => instance.interceptors.response.use(undefined, error => {
  if (error.config && error.response && error.response.status === HttpStatus.Unauthorized) {
    const {
      config
    } = error;
    return axios.post(REFRESH_ACCESS_TOKEN_API_ENDPOINT).then(() => instance.request(config));
  }

  return Promise.reject(error);
});
export const errorInterceptor = instance => instance.interceptors.response.use(response => {
  if (typeof response.data.code === 'undefined') {
    logWarning( // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    "API code is missing in response: ".concat(response.request.responseURL, " with status: ").concat(response.status));
  }

  if (response.data.code && response.data.code !== ResponseCode.Ok) {
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      response
    });
  }

  return Promise.resolve(response);
});
export const localeInterceptor = instance => instance.interceptors.request.use(config => {
  var _document$querySelect2;

  let locale = (_document$querySelect2 = document.querySelector('meta[name="accept-language"]')) === null || _document$querySelect2 === void 0 ? void 0 : _document$querySelect2.getAttribute('content');
  if (!locale) locale = getLocale();

  if (!locale) {
    logWarning('Unable to retrieve locale for API call');
  }

  if (locale) config.headers.set('Accept-Language', locale);
  return config;
});
export const phoneVerificationInterceptor = instance => instance.interceptors.response.use(undefined, error => {
  if (extractErrorResponseCode(error) === ResponseCode.UserVerificationRequired) {
    navigateToPage(PHONE_VERIFICATION_URL);
  }

  return Promise.reject(error);
});

function isTwoFaRequiredErrorResponse(error) {
  return extractErrorResponseCode(error) === ResponseCode.TwoFARequired;
}

export const twoFAInterceptor = instance => instance.interceptors.response.use(undefined, error => {
  if (!isTwoFaRequiredErrorResponse(error) || !error.response) return Promise.reject(error);
  const {
    response
  } = error;
  const event = new CustomEvent('twoFARequired', {
    detail: response.data.payload.entity_id
  });
  window.dispatchEvent(event);
  return new Promise((resolve, reject) => {
    window.addEventListener(TWO_FA_COMPLETED_EVENT, () => {
      instance.request(response.config).then(resolve).catch(reject);
    });
    window.addEventListener(TWO_FA_CANCELLED_EVENT, () => {
      reject(error);
    });
  });
});
export const anonIdInterceptor = instance => {
  instance.interceptors.request.use(config => {
    const anonId = getCookie(ANON_ID_COOKIE_NAME);
    config.headers.set(ANON_ID_HEADER_NAME, anonId);
    return config;
  });
};